import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Users, BookOpen, Heart, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  { icon: BookOpen, text: 'Access to exclusive Bible study materials' },
  { icon: Users, text: 'Connection with a supportive faith community' },
  { icon: Heart, text: 'Opportunities to serve and grow spiritually' },
];

export const Join = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    howHeard: '',
    acceptedBeliefs: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptedBeliefs) {
      toast.error('Please review and accept our Foundational Beliefs');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/members`, formData);
      setIsSubmitted(true);
      toast.success('Welcome to the family!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="join-success" className="min-h-screen pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-gold" size={48} />
          </div>
          <h1 className="font-cinzel text-3xl text-foreground mb-4">Welcome to the Family!</h1>
          <p className="text-muted-foreground mb-6">
            Thank you for joining True Believer. We'll be in touch soon with more information about our community and upcoming services.
          </p>
          <a href="/" className="btn-primary inline-flex items-center gap-2">
            Return Home
            <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="join-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-gold uppercase tracking-[0.3em] text-sm mb-4">Become a Member</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Join the <span className="text-gold">Fam</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Take the next step in your faith journey. Join a community dedicated to understanding and living out the true Word of God.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-cinzel text-2xl text-gold mb-8">Member Benefits</h2>
              <div className="space-y-6 mb-12">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 flex-shrink-0 rounded-full bg-gold/10 flex items-center justify-center">
                      <benefit.icon className="text-gold" size={24} />
                    </div>
                    <p className="text-foreground pt-3">{benefit.text}</p>
                  </div>
                ))}
              </div>

              {/* Foundational Beliefs Preview */}
              <div className="card-dark">
                <h3 className="font-cinzel text-lg text-foreground mb-4">Our Foundational Beliefs</h3>
                <ul className="space-y-3 text-muted-foreground text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-gold">•</span>
                    We believe in the Godhead: YAHWEH, Yeshua (Jesus), and the Holy Spirit
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold">•</span>
                    We teach scripture in its original context, free from denominational interpretation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold">•</span>
                    We reject division and embrace unity in Christ
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold">•</span>
                    We believe in applying faith through love and service
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Registration Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="join-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm text-foreground mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-firstName"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm text-foreground mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-lastName"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <label htmlFor="howHeard" className="block text-sm text-foreground mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    id="howHeard"
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleChange}
                    className="w-full bg-muted border border-border rounded-sm px-4 py-3 text-foreground focus:border-gold focus:outline-none transition-colors"
                    data-testid="select-howHeard"
                  >
                    <option value="">Select an option</option>
                    <option value="social">Social Media</option>
                    <option value="friend">Friend or Family</option>
                    <option value="search">Online Search</option>
                    <option value="event">Community Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptedBeliefs"
                    name="acceptedBeliefs"
                    checked={formData.acceptedBeliefs}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-gold"
                    data-testid="checkbox-beliefs"
                  />
                  <label htmlFor="acceptedBeliefs" className="text-sm text-muted-foreground">
                    I have reviewed and agree with True Believer's Foundational Beliefs *
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-join"
                >
                  {isSubmitting ? 'Submitting...' : 'Join the Family'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
